import { useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { TextField } from '../../../components/inputs/TextField';
import { ApiError } from '../../../lib/apiError';
import { focusValidationError } from '../../../lib/validationFocus';
import { cashCatalogsApi } from '../cashCatalogsApi';
import type { CashCatalogDescriptor, CashCatalogEntry } from '../cashCatalogTypes';
import { sampleCashCatalogEntry } from '../utils/sampleCashCatalogEntry';

/**
 * Create or edit one row of either Cash Division vocabulary.
 *
 * One modal for both, parameterised by the descriptor — they are one shape, and only the words
 * differ.
 */
export function CashCatalogModal({
  catalog,
  open,
  editing,
  onClose,
  onSaved,
}: {
  catalog: CashCatalogDescriptor;
  open: boolean;
  /** Null creates. */
  editing: CashCatalogEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { name: name.trim(), description: description.trim() || null };

    try {
      if (editing) await cashCatalogsApi.update(catalog.slug, editing.id, request);
      else await cashCatalogsApi.create(catalog.slug, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError(`Could not save this ${catalog.noun}.`, 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'cash-catalog' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleCashCatalogEntry(catalog.slug);

    setName(sample.name);
    setDescription(sample.description);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : `New ${catalog.noun}`}
      size="2xl"
      id="cash-catalog-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'cash-catalog-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="cash-catalog-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="cash-catalog-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="cash-catalog-name"
            label={catalog.columnHeader}
            required
            description={catalog.fieldDescription}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : `Enter the ${catalog.noun}'s name.`}
            disabled={saving}
          />

          <TextField
            id="cash-catalog-description"
            label="Description"
            description="What this covers. Read here, by whoever maintains the vocabulary — not by the clerk filling in a request."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={error?.fieldError('description')}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
